/**
 * CetusEd Admin Console — API Client
 *
 * COMPLIANCE: All admin API requests include the JWT bearer token with MFA
 *   verification claim. Admin endpoints enforce capability-based RBAC at the
 *   server level. Every mutation is audit-logged with the actor's identity.
 *
 *   FERPA §1232g(b)(4)(B) — Vendor support staff access to PII is limited
 *   to authorized support scenarios and MUST be audit-logged.
 */

/**
 * The admin API response envelope. @cetused/shared exports no ApiResponse
 * type — this mirrors the server's error-envelope contract
 * ({ success, data } | { success, error, code }).
 */
interface AdminApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
}

const API_BASE = '/api/admin';

let _adminToken: string | null = null;

export function setAdminToken(token: string | null): void {
  _adminToken = token;
}

export function getAdminToken(): string | null {
  return _adminToken;
}

export class AdminApiError extends Error {
  constructor(
    public readonly statusCode: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { signal?: AbortSignal | undefined },
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (_adminToken) {
    headers['Authorization'] = `Bearer ${_adminToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  });

  const json: AdminApiResponse<T> = await response.json();

  if (!response.ok || !json.success) {
    throw new AdminApiError(
      response.status,
      json.code || 'UNKNOWN',
      json.error || 'An unexpected error occurred',
      json.data,
    );
  }

  return json.data as T;
}

export const adminApi = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
